import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { GitBranch } from 'lucide-react';
import { Button, Callout, Modal, Spinner } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { AgentChip } from '@/modules/agents/components/AgentChip';
import { skillLabel } from '@/modules/agents/components/labels';
import type { AgentIdentity } from '@/modules/agents/utils/agentLabels';
import { Link } from 'react-router-dom';
import { registryApi, type RegistrySkill, type RegistrySkillDetail } from '../../api/registryApi';
import { overridesApi } from '../../api/overridesApi';
import { overrideKeys, registryKeys } from '../../queryKeys';
import { MODE_LABELS, describeTrigger } from '../../utils/registryView';
import { SkillChangesPanel } from './SkillChangesPanel';
import { SkillWriteBackPanel } from './SkillWriteBackPanel';
import styles from './SkillDetailDrawer.module.css';

function StatusPill({ skill }: { skill: RegistrySkill }) {
  if (skill.status === 'live') return <span className={patterns.pillSuccess}>Live</span>;
  return (
    <span className={patterns.pillMuted} title={skill.dormantReasons.join('; ')}>
      Dormant
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <span className={styles.metaLabel}>{label}</span>
      <span className={styles.metaValue}>{children}</span>
    </>
  );
}

function Detail({ skill, agents }: { skill: RegistrySkillDetail; agents: AgentIdentity[] }) {
  const references = Object.entries(skill.references);
  return (
    <>
      <div className={styles.head}>
        <div className={styles.chips}>
          <AgentChip
            agentType={skill.owner === 'shared' ? null : skill.agentType}
            agents={agents}
          />
          <span className={patterns.pillInfo}>{MODE_LABELS[skill.mode]}</span>
          <StatusPill skill={skill} />
          <span className={patterns.pillMuted}>
            {skill.methodFormat === 'none' ? 'No method body' : skill.methodFormat}
          </span>
          {skill.emitsCheckpoints && <span className={patterns.pillError}>Checkpoints</span>}
        </div>
        <div className={styles.title}>{skillLabel(skill.key)}</div>
        <div className={styles.key}>
          {skill.owner}/skills/{skill.dirName}
        </div>
        {skill.objective && <p className={styles.objective}>{skill.objective}</p>}
      </div>

      <div className={styles.body}>
        {skill.status === 'dormant' && (
          <Callout variant="warning" title="Dormant">
            {skill.dormantReasons.join(' · ')}
          </Callout>
        )}

        <div className={styles.meta}>
          <Row label="Agent">
            {skill.owner === 'shared' ? 'Shared across agents' : skill.agentType}
          </Row>
          {skill.integration && <Row label="Integration">{skill.integration}</Row>}
          {skill.auditType && <Row label="Audit type">{skill.auditType}</Row>}
          {skill.produces && <Row label="Produces">{skillLabel(skill.produces)}</Row>}
          <Row label="Tools">
            {skill.tools.length === 0 && <span className={styles.empty}>none</span>}
            {skill.tools.map((t) => (
              <span
                key={t}
                className={`${styles.mono} ${skill.missingTools.includes(t) ? styles.missing : skill.stubTools.includes(t) ? styles.stub : ''}`}
                title={
                  skill.stubTools.includes(t)
                    ? 'Registered but not connected yet: runs record this as a gap'
                    : undefined
                }
              >
                {t}
                {skill.stubTools.includes(t) ? ' (not connected)' : ''}
              </span>
            ))}
          </Row>
          {skill.requires.length > 0 && (
            <Row label="Requires">
              {skill.requires.map((t) => (
                <span
                  key={t}
                  className={`${styles.mono} ${skill.missingTools.includes(t) ? styles.missing : ''}`}
                >
                  {t}
                </span>
              ))}
            </Row>
          )}
          <Row label="Triggers">
            {skill.triggers.length === 0 && (
              <span className={styles.empty}>none — runs when routed to</span>
            )}
            {skill.triggers.map((t) => (
              <span key={t.key} className={styles.mono}>
                {describeTrigger(t)}
              </span>
            ))}
          </Row>
          {skill.routes.length > 0 && (
            <Row label="Routes">
              {skill.routes.map((r) => (
                <span key={r} className={styles.mono}>
                  {r}
                </span>
              ))}
            </Row>
          )}
          {Object.keys(skill.findingKinds).length > 0 && (
            <Row label="Writes findings">
              {Object.entries(skill.findingKinds).map(([source, kinds]) => (
                <span key={source} className={styles.mono}>
                  {source} ({kinds.length})
                </span>
              ))}
            </Row>
          )}
          <Row label="Evidence policy">
            {skill.hasEvidencePolicy
              ? skill.evidenceSubjects.length > 0
                ? skill.evidenceSubjects.map((s) => (
                    <span key={s} className={styles.mono}>
                      {s}
                    </span>
                  ))
                : 'declared, gates nothing'
              : 'none'}
          </Row>
          {skill.reportableLabel && <Row label="Owner sees it as">{skill.reportableLabel}</Row>}
          <Row label="References">{skill.referenceCount}</Row>
          <Row label="Practice overrides">
            <OverrideCount skillKey={skill.key} />
          </Row>
        </div>

        <div>
          <div className={styles.sectionLabel}>manifest.json</div>
          <pre className={styles.code}>{JSON.stringify(skill.manifest, null, 2)}</pre>
        </div>

        <div>
          <div className={styles.sectionLabel}>
            {skill.methodFormat === 'none' ? 'Method' : skill.methodFormat}
            <span className={styles.sectionHint}>rendered, frontmatter stripped</span>
          </div>
          {skill.method ? (
            <div className={styles.method}>
              <ReactMarkdown>{skill.method}</ReactMarkdown>
            </div>
          ) : (
            <p className={styles.empty}>This skill has no method body.</p>
          )}
        </div>

        {references.length > 0 && (
          <div>
            <div className={styles.sectionLabel}>References</div>
            {references.map(([name, body]) => (
              <details key={name} className={styles.reference}>
                <summary>{name}</summary>
                <div className={styles.method}>
                  <ReactMarkdown>{body}</ReactMarkdown>
                </div>
              </details>
            ))}
          </div>
        )}

        <WriteBack skill={skill} />
      </div>
    </>
  );
}

/** How many locations run a practice-specific version of this skill right now. */
function OverrideCount({ skillKey }: { skillKey: string }) {
  const overrides = useQuery({
    queryKey: overrideKeys.list(),
    queryFn: () => overridesApi.listAll(),
    staleTime: 60_000,
  });
  const count = (overrides.data ?? []).filter((o) => o.skillKey === skillKey).length;
  if (count === 0)
    return <span className={styles.empty}>none — every location runs the global skill</span>;
  return (
    <Link to="/agency/agents/overrides">
      {count} active override{count === 1 ? '' : 's'} ↗
    </Link>
  );
}

/** Edit / upload → validate → pull request, plus this skill's history of proposed changes. */
function WriteBack({ skill }: { skill: RegistrySkillDetail }) {
  const [panel, setPanel] = useState<'edit' | 'upload' | null>(null);
  const writeBack = useQuery({
    queryKey: registryKeys.writeBack(),
    queryFn: () => registryApi.getWriteBack(),
    staleTime: 5 * 60_000,
  });
  const files = useQuery({
    queryKey: registryKeys.files(skill.key),
    queryFn: () => registryApi.getSkillFiles(skill.key),
    enabled: panel === 'edit',
  });
  const configured = writeBack.data?.configured ?? false;

  return (
    <div>
      <div className={styles.sectionLabel}>
        Changes
        <span className={styles.sectionHint}>
          {configured
            ? `pull requests against ${writeBack.data?.repo} (${writeBack.data?.baseBranch})`
            : 'write-back not configured — read-only'}
        </span>
      </div>
      {panel === null && (
        <div className={styles.actions}>
          <Button size="sm" disabled={!configured} onClick={() => setPanel('edit')}>
            Edit skill
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!configured}
            onClick={() => setPanel('upload')}
          >
            Upload new version
          </Button>
        </div>
      )}
      {panel === 'edit' && files.isLoading && <Spinner />}
      {panel === 'edit' && files.isError && (
        <Callout variant="error" title="Could not load the skill's files">
          Try again in a moment.
        </Callout>
      )}
      {panel === 'edit' && files.data && (
        <SkillWriteBackPanel
          mode="edit"
          owner={files.data.owner}
          dirName={files.data.dirName}
          initialFiles={files.data.files}
          onCancel={() => setPanel(null)}
        />
      )}
      {panel === 'upload' && (
        <SkillWriteBackPanel
          mode="upload"
          owner={skill.owner}
          dirName={skill.dirName}
          onCancel={() => setPanel(null)}
        />
      )}
      <div className={styles.history}>
        <SkillChangesPanel owner={skill.owner} dirName={skill.dirName} bare />
      </div>
      {!configured && (
        <Callout icon={GitBranch} variant="info" title="GitHub is the source of truth">
          Edits from here become pull requests once the backend has a GitHub App installation
          (GITHUB_REPO and friends in the backend environment). Until then this screen reads.
        </Callout>
      )}
    </div>
  );
}

/**
 * Everything about one skill: what its manifest declares, the method body the agent reads, and the
 * references it may cite. Fetched on open, so the registry list stays light.
 */
export function SkillDetailDrawer({
  skillKey,
  agents,
  onClose,
}: {
  skillKey: string | null;
  agents: AgentIdentity[];
  onClose: () => void;
}) {
  const detail = useQuery({
    queryKey: registryKeys.skill(skillKey ?? ''),
    queryFn: () => registryApi.getSkill(skillKey!),
    enabled: !!skillKey,
  });

  return (
    <Modal isOpen={!!skillKey} onClose={onClose} title="Skill" size="lg" variant="side">
      {detail.isLoading && <Spinner />}
      {detail.isError && (
        <p className={patterns.cellMuted}>This skill could not be loaded from agent-suite.</p>
      )}
      {detail.data && <Detail skill={detail.data} agents={agents} />}
    </Modal>
  );
}
